/**
 * @fileoverview GameState - ゲーム状態管理システム
 */
import { DEFAULT_LIMIT_VALUES } from '../shared/constants/limit-defaults.js';

import { GameStatus } from './constants.js';
import { createDefaultSettings, createDefaultStats } from './settings-schema.js';

/**
 * @typedef {Object} StateChangeEvent
 * @property {string} path - 変更されたパス（例: 'core.status', 'ui.isSkillTargeting'）
 * @property {*} value - 変更後の値
 * @property {*} prevValue - 変更前の値
 * @property {string} action - 変更アクション（例: 'unknown', 'startGame', 'skillUsesUpdate'）
 * @property {number} timestamp - 変更時刻（Date.now()）
 */

/**
 * @callback StateChangeCallback
 * @param {StateChangeEvent} event - 状態変更イベント
 * @returns {void}
 */

class GameState {
  constructor() {
    this.state = this.createInitialState();
    this.listeners = new Set();
  }
  createInitialState() {
    return {
      core: { status: GameStatus.Title },
      ui: {
        isSkillTargeting: false,
        remainingSkillUses: 0,
        isDrawerOpen: false,
        tutorial: {
          status: 'idle',
          step: null,
        },
      },
      limit: {
        config: {
          timeLimitMs: DEFAULT_LIMIT_VALUES.TIME_LIMIT_MS,
          maxMoves: DEFAULT_LIMIT_VALUES.MAX_MOVES,
        },
        runtime: {
          phase: 'idle',
          totalActiveMs: 0,
        },
        onLimitReached: null,
      },
      game: { score: 0 },
      settings: createDefaultSettings(),
      stats: createDefaultStats(),
    };
  }

  /**
   * 状態の値を取得（オブジェクト/配列はコピーを返す）
   * @param {string|null} path - ドット区切りパス（nullで全状態を取得）
   * @returns {*} 取得した値（オブジェクトの場合はコピー）
   */
  getState(path = null) {
    if (path === null) return this.state; // 全体
    const value = this.getNestedValue(this.state, path);
    // プリミティブならそのまま、オブジェクトは浅コピー
    return value && typeof value === 'object' && !Array.isArray(value)
      ? { ...value }
      : Array.isArray(value)
        ? [...value]
        : value;
  }
  /**
   * 状態の値を更新し、必要な購読者に通知
   * @param {string} path - ドット区切りパス（例: 'core.status', 'ui.remainingSkillUses'）
   * @param {*} value - 設定する値
   * @param {string} action - 変更アクション（デバッグ用）
   * @returns {boolean} 更新が成功したか
   */
  setState(path, value, action = 'unknown') {
    // 変更前の値を取得
    const prevValue = this.getNestedValue(this.state, path);
    // インプレース更新
    this.setNestedValueInPlace(this.state, path, value);
    this.notifyListeners(path, value, prevValue, action);
    return true;
  }
  /**
   * 状態変更を購読
   * @param {StateChangeCallback} callback - 状態変更時に呼ばれるコールバック
   * @returns {() => void} unsubscribe関数
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }
  notifyListeners(path, value, prevValue, action) {
    for (const callback of this.listeners) {
      callback({ path, value, prevValue, action, timestamp: Date.now() });
    }
  }
  getNestedValue(obj, path) {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }
  setNestedValueInPlace(obj, path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    // 必要な中間階層だけ確保
    let current = obj;
    for (const key of keys) {
      if (!current[key] || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }
    current[lastKey] = value;
  }
}
const gameState = new GameState();
export default gameState;
